import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { businessApi } from '../../api/businessApi';
import { toast } from 'react-hot-toast';
import { 
  BuildingStorefrontIcon, 
  TrashIcon, 
  PlusIcon, 
  CheckBadgeIcon, 
  PhotoIcon 
} from '@heroicons/react/24/outline';
import { uploadFile } from '../../utils/fileUpload';

export default function Businesses() {
  const { user, businesses, activeBusiness, switchBusiness, loadBusinesses, isOwner } = useAuth();

  // "NEW" means creating a new business, otherwise holds the ID of the business being edited
  const [selectedId, setSelectedId] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  
  const [form, setForm] = useState({
    name: '', type: 'retail', address: '', gstNumber: '', email: '', phone: '', upiId: '', logoUrl: '', bannerUrl: ''
  });

  // Select the first business by default, or keep "NEW" if creating
  useEffect(() => {
    if (!selectedId && businesses.length > 0) {
      handleSelect(businesses[0]);
    }
  }, [businesses]);

  const handleSelect = (b) => {
    if (b === 'NEW') {
      const canAddMultiple = user?.subscription?.features?.multipleBusiness;
      if (!canAddMultiple && businesses.length >= 1) {
        window.dispatchEvent(new CustomEvent('showUpgradeModal', {
          detail: { message: `You have reached the limit of 1 khata on your current plan. Upgrade to Premium to manage multiple khatas.` }
        }));
        return;
      }
      setSelectedId('NEW');
      setForm({ name: '', type: 'retail', address: '', gstNumber: '', email: '', phone: '', upiId: '', logoUrl: '', bannerUrl: '' });
    } else {
      setSelectedId(b._id);
      setForm({
        name: b.name,
        type: b.type || 'retail',
        address: b.address?.street || '',
        gstNumber: b.gstNumber || '',
        email: b.contact?.email || '',
        phone: b.contact?.phone || '',
        upiId: b.payment?.upiId || '',
        logoUrl: b.assets?.logoUrl || '',
        bannerUrl: b.assets?.bannerUrl || ''
      });
    }
  };

  const handleImageUpload = async (e, field) => {
    const file = e.target.files[0];
    if (!file) return;

    const toastId = toast.loading(`Uploading ${field}...`);
    try {
      const url = await uploadFile(file, 'business_assets');
      if (url) {
        setForm(prev => ({ ...prev, [field]: url }));
        toast.dismiss(toastId);
      } else {
        toast.error(`Failed to upload ${field}`, { id: toastId });
      }
    } catch (err) {
      toast.error(`Upload error`, { id: toastId });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      if (selectedId === 'NEW') {
        const res = await businessApi.createBusiness(form);
        if (res.data.success) {
          toast.success('Business created successfully!');
          await loadBusinesses();
          handleSelect(res.data.data);
        }
      } else {
        const res = await businessApi.updateBusiness(selectedId, form);
        if (res.data.success) {
          toast.success('Business updated successfully!');
          await loadBusinesses();
        }
      }
    } catch (err) {
      if (!err.response?.data?.upgradeRequired) {
        toast.error(err.response?.data?.message || 'Failed to save business');
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this Khata? It will be archived.')) return;
    try {
      const res = await businessApi.deleteBusiness(selectedId);
      if (res.data.success) {
        toast.success('Business deleted');
        await loadBusinesses();
        setSelectedId(null);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete business');
    }
  };

  return (
    <div className="max-w-7xl mx-auto h-[calc(100vh-120px)] flex flex-col md:flex-row gap-6 pb-6">
      
      {/* Left Panel: List */}
      <div className="w-full md:w-1/3 flex flex-col bg-white dark:bg-[#0f172a] rounded-2xl shadow-sm border border-gray-100 dark:border-gray-800 overflow-hidden">
        <div className="p-4 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between">
          <h2 className="text-lg font-extrabold text-gray-900 dark:text-white">Your Khatas</h2>
        </div>
        
        {isOwner && (
          <div className="p-4 border-b border-gray-100 dark:border-gray-800">
            <button 
              onClick={() => handleSelect('NEW')}
              className="w-full flex items-center justify-center gap-2 bg-[#dc343b] text-white px-4 py-2.5 rounded-xl font-bold hover:bg-red-700 transition-colors shadow-sm"
            >
              <PlusIcon className="w-5 h-5" />
              <span>Create New Khata</span>
            </button>
          </div>
        )}

        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {businesses.map(b => (
            <div 
              key={b._id}
              onClick={() => handleSelect(b)}
              className={`p-4 rounded-xl border cursor-pointer transition-all ${selectedId === b._id ? 'border-[#dc343b] bg-red-50/50 dark:bg-gray-800 ring-1 ring-[#dc343b]' : 'border-gray-100 dark:border-gray-800 hover:border-gray-300 dark:hover:border-gray-700'}`}
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 shrink-0 rounded-lg bg-gray-100 dark:bg-gray-800 flex items-center justify-center overflow-hidden border border-gray-200 dark:border-gray-700">
                  {b.assets?.logoUrl ? (
                    <img src={b.assets.logoUrl} alt="Logo" className="w-full h-full object-cover" />
                  ) : (
                    <BuildingStorefrontIcon className="w-6 h-6 text-gray-400" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="font-bold text-gray-900 dark:text-white truncate">{b.name}</h4>
                  <p className="text-xs text-gray-500 uppercase tracking-wide truncate">{b.type}</p>
                </div>
                {activeBusiness?._id === b._id && (
                  <CheckBadgeIcon className="w-6 h-6 text-green-500 shrink-0" title="Active Business" />
                )}
              </div>
            </div>
          ))}
          {businesses.length === 0 && (
            <div className="text-center py-10 text-gray-400 text-sm">No businesses found.</div>
          )}
        </div>
      </div>

      {/* Right Panel: Form */}
      <div className="flex-1 bg-white dark:bg-[#0f172a] rounded-2xl shadow-sm border border-gray-100 dark:border-gray-800 overflow-hidden flex flex-col">
        {selectedId ? (
          <form onSubmit={handleSubmit} className="flex flex-col h-full">
            
            {/* Banner Area */}
            <div className="relative h-48 bg-gray-100 dark:bg-gray-800 shrink-0">
              {form.bannerUrl ? (
                <img src={form.bannerUrl} alt="Banner" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-gray-400 flex-col gap-2">
                  <PhotoIcon className="w-8 h-8" />
                  <span className="text-sm font-medium">Add Cover Photo</span>
                </div>
              )}
              <label className="absolute bottom-4 right-4 bg-white/90 dark:bg-gray-900/90 backdrop-blur px-4 py-2 rounded-xl text-sm font-bold text-gray-700 dark:text-white cursor-pointer shadow-sm hover:scale-105 transition-transform">
                Change Banner
                <input type="file" className="hidden" accept="image/*" onChange={(e) => handleImageUpload(e, 'bannerUrl')} />
              </label>
              
              {/* Logo Positioning */}
              <div className="absolute -bottom-10 left-8">
                <div className="relative w-24 h-24 rounded-2xl bg-white dark:bg-gray-900 shadow-md border-4 border-white dark:border-[#0f172a] overflow-hidden flex items-center justify-center group">
                  {form.logoUrl ? (
                    <img src={form.logoUrl} alt="Logo" className="w-full h-full object-cover" />
                  ) : (
                    <BuildingStorefrontIcon className="w-8 h-8 text-gray-300" />
                  )}
                  <label className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 cursor-pointer transition-opacity">
                    <PhotoIcon className="w-6 h-6 text-white" />
                    <input type="file" className="hidden" accept="image/*" onChange={(e) => handleImageUpload(e, 'logoUrl')} />
                  </label>
                </div>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto px-8 pt-16 pb-8">
              <div className="flex items-center justify-between mb-8">
                <div>
                  <h2 className="text-2xl font-extrabold text-gray-900 dark:text-white">
                    {selectedId === 'NEW' ? 'Create New Khata' : 'Business Settings'}
                  </h2>
                  <p className="text-sm text-gray-500 mt-1">Configure your business details and payment info.</p>
                </div>
                {selectedId !== 'NEW' && activeBusiness?._id !== selectedId && (
                  <button type="button" onClick={() => switchBusiness(selectedId)} className="bg-red-50 text-[#dc343b] px-4 py-2 rounded-xl text-sm font-bold hover:bg-red-100 transition-colors">
                    Set as Active Khata
                  </button>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Basic Info */}
                <div className="space-y-4 md:col-span-2">
                  <h3 className="text-sm font-bold text-gray-900 dark:text-white uppercase tracking-wider border-b border-gray-100 dark:border-gray-800 pb-2">Basic Info</h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Business Name *</label>
                      <input type="text" required value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="w-full px-4 py-2 border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-[#dc343b] outline-none" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Business Type</label>
                      <select value={form.type} onChange={e => setForm({...form, type: e.target.value})} className="w-full px-4 py-2 border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-[#dc343b] outline-none">
                        <option value="retail">Retail</option>
                        <option value="wholesale">Wholesale</option>
                        <option value="service">Service</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Address</label>
                      <input type="text" value={form.address} onChange={e => setForm({...form, address: e.target.value})} className="w-full px-4 py-2 border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-[#dc343b] outline-none" />
                    </div>
                  </div>
                </div>

                {/* Contact & Legal */}
                <div className="space-y-4">
                  <h3 className="text-sm font-bold text-gray-900 dark:text-white uppercase tracking-wider border-b border-gray-100 dark:border-gray-800 pb-2">Contact & Legal</h3>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Phone</label>
                    <input type="text" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} className="w-full px-4 py-2 border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-[#dc343b] outline-none" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Email</label>
                    <input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="w-full px-4 py-2 border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-[#dc343b] outline-none" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">GSTIN</label>
                    <input type="text" value={form.gstNumber} onChange={e => setForm({...form, gstNumber: e.target.value})} className="w-full px-4 py-2 border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-[#dc343b] outline-none uppercase font-mono" placeholder="22AAAAA0000A1Z5" />
                  </div>
                </div>

                {/* Payments */}
                <div className="space-y-4">
                  <h3 className="text-sm font-bold text-gray-900 dark:text-white uppercase tracking-wider border-b border-gray-100 dark:border-gray-800 pb-2">Payments</h3>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">UPI ID (VPA)</label>
                    <input type="text" value={form.upiId} onChange={e => setForm({...form, upiId: e.target.value})} className="w-full px-4 py-2 border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-[#dc343b] outline-none" placeholder="merchant@upi" />
                    <p className="text-xs text-gray-500 mt-1">This UPI ID will be used for QR codes and payment links.</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer Actions */}
            <div className="p-4 border-t border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-900/50 flex justify-between items-center shrink-0">
              {selectedId !== 'NEW' && businesses.length > 1 ? (
                <button type="button" onClick={handleDelete} className="flex items-center gap-2 text-red-600 hover:text-red-700 font-bold px-4 py-2 rounded-xl hover:bg-red-50 transition-colors">
                  <TrashIcon className="w-5 h-5" /> Delete Khata
                </button>
              ) : <div></div>}
              
              <button type="submit" disabled={isSaving} className="bg-[#dc343b] text-white px-8 py-2.5 rounded-xl font-bold hover:bg-red-700 transition-colors disabled:opacity-50">
                {isSaving ? 'Saving...' : (selectedId === 'NEW' ? 'Create Khata' : 'Save Changes')}
              </button>
            </div>
          </form>
        ) : (
          <div className="flex-1 flex items-center justify-center text-gray-400 text-sm">
            Select a Khata from the left or create a new one.
          </div>
        )}
      </div>
    </div>
  );
}
